import {
  AlertCircle,
  FileText,
  MoreVertical,
  Pencil,
  Trash2,
} from "lucide-react";
import type { MouseEvent } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from "@/components/ui/item";
import { cn } from "@/lib/utils";
import type { CollectionKind, ProjectReport } from "@/types/api";

import { startReportDownload } from "./download";
import { FormatBadge, FormatPlaceholderIcon } from "./format-badge";
import { formatLabel } from "./report-format";
import { ReportProgressBar } from "./report-progress-bar";
import { useReportProgress } from "./use-report-progress";

interface ReportItemProps {
  report: ProjectReport;
  /**
   * What the report holds, for the in-flight phase wording. Photos when
   * omitted — every report made before issues reports existed.
   */
  kind?: CollectionKind;
  creatorName: string;
  canRename: boolean;
  canDelete: boolean;
  onRename: (report: ProjectReport) => void;
  onDelete: (report: ProjectReport) => void;
}

function formatDate(value: string | null): string | null {
  if (!value) return null;
  return new Date(value).toLocaleDateString("en-SG", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
}

export function ReportItem({
  report,
  kind = "records",
  creatorName,
  canRename,
  canDelete,
  onRename,
  onDelete,
}: ReportItemProps) {
  // The simulation drives the in-progress UI and tells us when to hand off
  // to the completed layout (see report-item.spec "Completing" state). It's
  // safe to mount unconditionally — the hook is a no-op for terminal
  // statuses except for clearing persisted start time on failure.
  const progress = useReportProgress(
    report.id,
    report.status,
    report.record_count,
    kind,
  );

  const statusInProgress =
    report.status === "PENDING" || report.status === "PROCESSING";
  // Hold the in-progress visual until the bar settles (`isDone`) so the
  // completion handoff is one smooth motion. Only flips early on FAILED.
  const isInProgress =
    statusInProgress ||
    (report.status === "COMPLETED" && !progress.isDone);
  const isFailed = report.status === "FAILED";
  const isCompleted = !isInProgress && !isFailed;

  const hasFile = isCompleted && !!report.file;
  // PDFs open inline in a new tab — every browser (including the iOS in-app
  // viewer an installed PWA falls back to) renders a PDF inline. Every other
  // format (PPTX today; DOCX/XLSX later) has no inline renderer, so it's a
  // direct download instead: the API pre-signs the non-PDF `file` URL with
  // `Content-Disposition: attachment`, so a plain link triggers the browser's
  // native download on every platform — no progress dialog, no share sheet.
  const opensInline = hasFile && report.format === "pdf";
  const opensViaDownload = hasFile && report.format !== "pdf";

  const showCompletedMenu = isCompleted && (canRename || canDelete);
  const showFailedMenu = isFailed && canDelete;

  // Intercept the download tap to flash the heads-up toast, then hand off to
  // the native download after a short beat. Bail on modified/secondary clicks
  // so the browser's own "save link as" / middle-click behaviour is preserved.
  const handleDownloadClick = (event: MouseEvent<HTMLAnchorElement>) => {
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey
    ) {
      return;
    }
    event.preventDefault();
    startReportDownload(report.file!, report.name);
  };

  const body = (
    <>
      <ReportThumbnail
        report={report}
        isInProgress={isInProgress}
        isFailed={isFailed}
      />
      <ReportContent
        report={report}
        creatorName={creatorName}
        formattedDate={formatDate(report.created_at)}
        isInProgress={isInProgress}
        isFailed={isFailed}
        progress={progress}
      />
    </>
  );

  return (
    <Item
      variant="outline"
      className={cn(
        "w-full p-0 overflow-hidden",
        isFailed && "border-l-2 border-destructive",
      )}
    >
      <div
        className={cn(
          "flex h-24 w-full flex-row items-stretch",
          isInProgress && "pointer-events-none opacity-80",
        )}
      >
        {opensInline ? (
          <a
            href={report.file!}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`View report ${report.name} (${formatLabel(report.format)})`}
            className="flex min-w-0 flex-1 flex-row items-stretch"
          >
            {body}
          </a>
        ) : opensViaDownload ? (
          <a
            href={report.file!}
            download={report.name}
            onClick={handleDownloadClick}
            aria-label={`Download report ${report.name} (${formatLabel(report.format)})`}
            className="flex min-w-0 flex-1 flex-row items-stretch"
          >
            {body}
          </a>
        ) : (
          <div className="flex min-w-0 flex-1 flex-row items-stretch">
            {body}
          </div>
        )}

        {(showCompletedMenu || showFailedMenu) && (
          <div className="flex items-center pr-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  aria-label="Report options"
                >
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {showCompletedMenu && canRename && (
                  <DropdownMenuItem onClick={() => onRename(report)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Rename
                  </DropdownMenuItem>
                )}
                {(showCompletedMenu || showFailedMenu) && canDelete && (
                  <DropdownMenuItem
                    variant="destructive"
                    onClick={() => onDelete(report)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      </div>
    </Item>
  );
}

function ReportThumbnail({
  report,
  isInProgress,
  isFailed,
}: {
  report: ProjectReport;
  isInProgress: boolean;
  isFailed: boolean;
}) {
  const thumbnailSrc = report.thumbnail;
  const isCompleted = !isInProgress && !isFailed;

  return (
    <div className="relative flex h-full w-20 shrink-0 items-center justify-center border-r bg-muted">
      {isInProgress && (
        <FileText className="h-6 w-6 text-muted-foreground" />
      )}
      {isFailed && <AlertCircle className="h-6 w-6 text-destructive" />}
      {isCompleted && thumbnailSrc && (
        <img
          src={thumbnailSrc}
          alt=""
          crossOrigin="anonymous"
          className="absolute inset-0 h-full w-full object-cover"
        />
      )}
      {/* Image-less completed rows fall back to a format-specific glyph (a deck
          shows a Presentation icon, a sheet a spreadsheet icon) so the format
          reads even before the corner chip. */}
      {isCompleted && !thumbnailSrc && (
        <FormatPlaceholderIcon
          format={report.format}
          className="h-6 w-6 text-muted-foreground"
        />
      )}
      {/* Format chip: completed rows only — format is moot while a report is
          still generating or after it has failed. */}
      {isCompleted && <FormatBadge format={report.format} />}
    </div>
  );
}

function ReportContent({
  report,
  creatorName,
  formattedDate,
  isInProgress,
  isFailed,
  progress,
}: {
  report: ProjectReport;
  creatorName: string;
  formattedDate: string | null;
  isInProgress: boolean;
  isFailed: boolean;
  progress: ReturnType<typeof useReportProgress>;
}) {
  return (
    <ItemContent className="flex min-w-0 flex-1 flex-col justify-center p-4">
      <ItemTitle className="truncate max-w-full">{report.name}</ItemTitle>
      {isInProgress && (
        <div className="mt-1 max-w-full">
          <ReportProgressBar progress={progress} />
        </div>
      )}
      {isFailed && (
        <ItemDescription className="text-destructive">
          {report.error_message || "Report generation failed"}
        </ItemDescription>
      )}
      {!isInProgress && !isFailed && (
        <>
          {formattedDate && <ItemDescription>{formattedDate}</ItemDescription>}
          <ItemDescription>By {creatorName}</ItemDescription>
        </>
      )}
    </ItemContent>
  );
}
